/**
 * One question at a time.
 *
 * Two dialogs in this app are about the **device** rather than about the game:
 * `RotatePrompt` ("turn the phone sideways") and `FullscreenPrompt` ("play
 * fullscreen"). Both are modal, both cover the whole app, and both sit high in
 * the stacking order because what they are asking for has to be settled before
 * anything else on the screen is worth reading. `FullscreenPrompt` already
 * encodes half of that rule in its own conditions — it is landscape-only
 * precisely so it cannot stack on top of the rotate prompt — and its header
 * states the principle: *rotate first, then this — one question at a time.*
 *
 * Nothing extended that rule to the third dialog, and it cost a dead button.
 *
 * ── The bug this exists to close ────────────────────────────────────────────
 *
 * The garage's basic-training prompt (`garage/TrainingPrompt.vue`) is `z-index:
 * 40`; the fullscreen offer is 90. So on a freshly registered account opened on
 * a phone in landscape — which is *every* new phone player, since the fullscreen
 * offer has not been declined yet and the training prompt is derived from an
 * account that has done nothing — both were up at once, and the training
 * prompt's *Start basic training* button was visible through the fullscreen
 * offer's 82%-opaque scrim while every tap on it landed on that scrim instead.
 * Measured with `elementFromPoint` at the button's own centre: `div.fs-prompt`.
 *
 * That is the worst shape a defect can take — the control is on screen, it looks
 * enabled, and pressing it does nothing at all — and it was reported exactly that
 * way. A z-index shuffle is the wrong repair: it would put the training prompt on
 * top of a rotate prompt that has to be answered first, and swap which of the two
 * buttons is dead rather than removing the collision.
 *
 * ── What this module is ─────────────────────────────────────────────────────
 *
 * A count of how many device prompts are currently showing, and nothing else.
 * Each prompt registers its own `showing` — the condition stays in the component
 * that owns it, because those conditions are about media queries and the document
 * and belong beside the listeners that re-read them — and anything that must
 * queue behind them reads `devicePromptOpen`.
 *
 * A set of conditions rather than a set of booleans this module maintains, so the
 * answer is **derived** and not notified: a registry that watched each `showing`
 * and kept a count of its own would settle a tick late (Vue flushes watchers on
 * the microtask queue), which is one rendered frame of a dialog drawn underneath
 * a scrim — the exact state this exists to make impossible. Derived, the two
 * cannot disagree at any instant. Same discipline as the fuel gauge and the
 * research countdown: store the inputs, compute the answer on read.
 *
 * A set rather than a boolean per prompt, so a third device prompt is one call
 * rather than a condition every reader has to be taught about. And a registry
 * rather than a recomputation of the same media queries here: two copies of "am I
 * a handheld in portrait" would agree on the day they were written and drift on
 * some later one, which is the failure `touch-layout.ts` exists to avoid one
 * surface along.
 */
import { computed, onScopeDispose, reactive, type Ref } from 'vue';

/** Every device prompt's own visibility condition, while its component lives. */
const conditions = reactive(new Set<Ref<boolean>>());

/**
 * Is a device prompt up?
 *
 * Read by anything that must not draw over one — today the garage's training
 * prompt. It is deliberately *not* consulted by the two device prompts
 * themselves: their mutual exclusion is already structural (portrait versus
 * landscape) and making them read this as well would be a cycle.
 */
export const devicePromptOpen = computed(() => {
  for (const showing of conditions) if (showing.value) return true;
  return false;
});

/**
 * Registers a device prompt's visibility for as long as the component lives.
 *
 * The component's own scope releases it, which is the half that would rot in
 * silence: a prompt unmounted *while showing* — leaving the garage with the
 * fullscreen offer up — would otherwise leave the queue blocked behind a dialog
 * nobody can see, for the rest of the session.
 */
export function useDevicePrompt(showing: Ref<boolean>): void {
  conditions.add(showing);
  onScopeDispose(() => {
    conditions.delete(showing);
  });
}

/** Test-only reset, so one spec's leftover registration cannot fail the next. */
export function resetDevicePrompts(): void {
  conditions.clear();
}
